<template>
  <modal
    ref="modal"
    modal-title="Generate completion certificates"
    modal-class="generate-completion-certificates-modal"
  >
    <template #modal-content>
      <template v-if="isWaiting">
        <p class="text-center mt-4">You are about to generate {{ participantIds.length }} completion certificates</p>
        <div class="text-center mt-4 mb-4">
          <button
            class="btn btn-primary"
            @click.prevent="generateCertificates"
          >
            Generate
          </button>
        </div>
      </template>
      <template v-else-if="isRunning">
        <p
          v-if="currentItem"
          class="text-center"
        >
          Generating certificate {{ currentItem }} of {{ totalItems }}
        </p>
        <p
          v-else
          class="text-center"
        >
          Generating certificates ...
        </p>
        <progress-indicator
          :progress="generateProgress"
        />
      </template>
      <template v-else>
        <div
          v-if="generatedItems > 0"
          class="alert alert-success text-center mt-3"
        >
          {{ generatedItems }} certificates has been generated!
        </div>
        <div
          v-else
          class="alert alert-danger text-center mt-3"
        >
          No certificates has been generated
        </div>
        <div
          v-for="(error, errorIdx) of errors"
          :key="errorIdx"
          class="alert alert-danger text-center mt-3"
        >
          {{ error }}
        </div>
      </template>
    </template>
    <template #modal-footer>
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        Close
      </button>
    </template>
  </modal>
</template>
<script setup lang="ts">
import {computed, ref, useTemplateRef} from 'vue'

import Modal from './modal.vue'
import ProgressIndicator from './progress-indicator.vue'

import {getValueNum, getValueStrNullable, isNonEmptyString} from '../js-utils'
import type {GridID} from './types'

enum Status {
  waiting = 0,
  running = 1,
  finished = 2
}

interface GenerateCertificatesResponse {
  status_url?: string | null;
}

interface CheckStatusResponse {
  current_item?: number;
  errors?: unknown;
  finished?: boolean;
  generated_items?: number | null;
  progress?: string | number;
  total_items?: number;
}

const props = defineProps({
  generateUrl: {default: null, type: String},
  participantIds: {default: (): GridID[] => [], type: Array as () => GridID[]},
})

const emit = defineEmits<{
  'certificates-generated': []
}>()

const modal = useTemplateRef<InstanceType<typeof Modal>>('modal')

const status = ref(Status.waiting)
const currentItem = ref(0)
const totalItems = ref(0)
const generateProgress = ref(0)
const statusUrl = ref<string | null>(null)
const errors = ref<string[]>([])
const generatedItems = ref(0)

const isWaiting = computed((): boolean => status.value === Status.waiting)

const isRunning = computed((): boolean => status.value === Status.running)

const checkStatus = (): void => {
  if (isNonEmptyString(statusUrl.value)) {
    modal.value?.performRequest<CheckStatusResponse>(statusUrl.value, null, {withLoadingIndicator: false}, (isSuccess, data) => {
      if (isSuccess) {
        if (data?.finished) {
          status.value = Status.finished
          generatedItems.value = getValueNum(data.generated_items || null, 0) || 0
          errors.value = Array.isArray(data.errors) ? data.errors : []
          emit('certificates-generated')
        } else {
          generateProgress.value = Math.round(Number.parseFloat(String(data?.progress || 0)) * 100)
          currentItem.value = data?.current_item || 0
          totalItems.value = data?.total_items || 0

          window.setTimeout(() => {
            checkStatus()
          }, 1000)
        }
      }
    })
  }
}

const generateCertificates = (): void => {
  modal.value?.hideError()
  modal.value?.performRequest<GenerateCertificatesResponse>(props.generateUrl, {
    participant_ids: props.participantIds
  }, {withLoadingIndicator: false}, (isSuccess, data) => {
    if (isSuccess) {
      status.value = Status.running
      generateProgress.value = 0
      statusUrl.value = getValueStrNullable(data?.status_url || null)
      if (isNonEmptyString(statusUrl.value)) {
        checkStatus()
      }
    }
  })
}

const showModal = (): void => {
  errors.value = []
  currentItem.value = 0
  totalItems.value = 0
  generateProgress.value = 0
  statusUrl.value = null
  status.value = Status.waiting

  modal.value?.hideLoadingIndicator()
  modal.value?.showModal()
}

defineExpose({
  showModal,
})
</script>
